import { search, searchCandidates, searchUsers } from '~/admin/api';
import { getUsers, searchTable } from '~/admin/api.new';
import { getTerminationReason } from '~/admin/contractors/api';
import { getRecordingUrl } from '~/admin/recordings/util';
import type { Filter } from '~/search';
import { searchTags } from '~/tags';
import {
    Agreement,
    Application,
    BankAccount,
    Bonus,
    Candidate,
    Contract,
    ContractorInvoice,
    Customer,
    CustomerInvoice,
    Document,
    EmailTemplate,
    Equipment,
    Expense,
    HealthInsuranceCost,
    InvoiceLine,
    JobOpening,
    JobOpeningRole,
    JobOpeningStage,
    OfferLetter,
    Payment,
    PTOPolicy,
    PTORequest,
    Raise,
    Recording,
    Referral,
    SecurityDeposit,
    StartupApplication,
    Tag,
    TagCategory,
    User,
    Webhook,
} from '~/types/domain';

type Row = Record<string, any>;

interface DefinitionEdit<T> {
    hide?: (keyof T)[];
    delete?: (keyof T)[];
    required?: (keyof T)[];
    required_marketing?: string[];
    [key: string]: any;
}

interface DefinitionEntry<T> {
    list?: {
        fields?: (keyof T)[];
        important?: (keyof T)[];
        picture?: string;
        view?: Partial<Record<keyof T, (value: any) => any>>;
        hide?: (keyof T)[];
    };
    edit?: DefinitionEdit<T>;
    related?: Array<{
        table: string;
        field: string;
        parent_field?: string;
        clickable?: boolean;
        preload?: boolean;
        options?: Record<string, string>;
        filters?: Filter[];
    }>;
    details?: {
        extend?: (data: T, schema?: any) => Promise<void> | void;
    };
    search?: (input: string, options?: Record<string, any>) => Filter[];
    customSearch?: (input: string, opts?: Record<string, any>) => Promise<Row[]>;
    display?: (row: T) => string;
    expand?: boolean;
    protected?: boolean;
    disableEdit?: boolean;
    disableCreate?: boolean;
    disableDelete?: boolean;
    disableExport?: boolean;
    beforeUpdate?: (data: T) => void;
    preload?: (opts?: Record<string, any>) => Filter[];
    important?: string[];
}

const definitions: Record<string, DefinitionEntry<any>> = {
    health_insurance_costs: {
        list: {
            fields: ['id', 'country', 'cost'],
            important: ['country'],
        },
    } as DefinitionEntry<HealthInsuranceCost>,
    security_deposits: {
        list: {
            fields: ['id', 'customer_id', 'contract_id', 'status', 'periodicity', 'amount'],
        },
    } as DefinitionEntry<SecurityDeposit>,
    equipment: {
        disableCreate: true,
        disableDelete: true,
        disableEdit: true,
        list: {
            fields: [
                'id',
                'status',
                'kind',
                'brand',
                'model',
                'part_number',
                'serial_number',
                'cost',
                'purchased_at',
                'contract_id',
            ],
            important: ['id', 'kind'],
        },
        edit: {
            description: { 'ui:widget': 'textarea' },
            hide: [
                'reminders',
                'expected_delivery',
                'finished_delivery',
                'order_number',
                'purchased_at',
                'delivered_at',
                'ordered_at',
                'confirmed_at',
                'provider_id',
                'termination_status',
                'customer_invoice_id',
            ],
            delete: [
                'reminders',
                'expected_delivery',
                'finished_delivery',
                'order_number',
                'purchased_at',
                'delivered_at',
                'ordered_at',
                'confirmed_at',
                'provider_id',
                'termination_status',
                'customer_invoice_id',
            ],
        },
        search(input) {
            const value = `%${input}%`;
            return [
                { type: 'OR', field: 'model', value, operator: 'like' },
                { type: 'OR', field: 'serial_number', value, operator: 'like' },
                { type: 'OR', field: 'brand', value, operator: 'like' },
            ];
        },
        display: (row) => `${row.brand} ${row.model} (${row.serial_number})`,
    } as DefinitionEntry<Equipment>,
    agreements: {
        list: {
            fields: ['id', 'entity_table', 'entity_id', 'type'],
            important: ['id'],
        },
    } as DefinitionEntry<Agreement>,
    contractor_invoices: {
        list: {
            fields: ['id', 'user_id', 'number', 'status', 'amount', 'month', 'year'],
            important: ['id', 'user_id'],
        },
        disableCreate: true,
        edit: {
            issued_by: { 'ui:widget': 'textarea' },
            billed_to: { 'ui:widget': 'textarea' },
        },
        related: [
            {
                table: 'invoice_lines',
                field: 'invoice_id',
                filters: [
                    {
                        field: 'type',
                        value: 'contractor_invoices',
                        operator: 'equals',
                        type: 'AND',
                    },
                ],
            },
        ],
    } as DefinitionEntry<ContractorInvoice>,
    documents: {
        list: {
            fields: ['id', 'name'],
            important: ['id'],
        },
        display: (row) => row.name,
    } as DefinitionEntry<Document>,
    customers: {
        list: {
            fields: ['id', 'company_name', 'status'],
            important: ['id', 'company_name'],
        },
        edit: {
            logo: { 'ui:widget': 'picture' },
            header: { 'ui:widget': 'picture' },
        },
        search(input) {
            const value = `%${input}%`;
            return [{ type: 'AND', field: 'company_name', value, operator: 'like' }];
        },
        display: (row) => row.company_name,
        related: [
            {
                table: 'users',
                field: 'id',
                parent_field: 'billing_contacts_ids',
                clickable: true,
            },
        ],
    } as DefinitionEntry<Customer>,
    tags: {
        list: {
            fields: ['id', 'name', 'category', 'alternatives', 'children'],
            important: ['id', 'name'],
        },
        async customSearch(input, opts = {}) {
            const rows = await searchTags(input, (opts.category || 'skill') as TagCategory);
            if (window.location.pathname.startsWith('/admin/backoffice')) {
                return rows.map((t) => ({ ...t, name: `${t.name} (${t.category})` }));
            }
            return rows;
        },
        display: (row) => row.name,
        preload(opts = {}) {
            if (opts.category) {
                return [
                    {
                        field: 'category',
                        operator: 'equals',
                        value: opts.category,
                        type: 'AND' as const,
                    },
                ];
            }
            return [];
        },
        related: [
            {
                table: 'tags',
                field: 'id',
                parent_field: 'children',
            },
        ],
    } as DefinitionEntry<Tag>,
    raises: {
        list: {
            fields: ['id', 'contract_id', 'old', 'new'],
            important: ['id'],
        },
        edit: {
            comment: { 'ui:widget': 'textarea' },
            hide: ['old', 'status', 'reviewed_at', 'reviewer_id', 'deposit_id', 'reviewer_table'],
        },
    } as DefinitionEntry<Raise>,
    referrals: {
        list: {
            fields: ['id', 'user_id', 'referrer_id', 'status', 'amount', 'paid_at'],
            important: ['id'],
        },
    } as DefinitionEntry<Referral>,
    offer_letters: {
        list: {
            fields: [
                'id',
                'email',
                'customer_id',
                'first_name',
                'last_name',
                'status',
                'role',
                'salary',
            ],
            important: ['id', 'email'],
        },
        edit: {
            hide: ['email_normalized'],
        },
    } as DefinitionEntry<OfferLetter>,
    job_openings: {
        disableCreate: true,
        disableEdit: true,
        disableDelete: true,
        list: {
            fields: ['id', 'title', 'openings', 'customer_id', 'hiring_manager_id', 'tags'],
            important: ['id', 'title'],
        },
        display: (data) => {
            let name = data.title;
            if ((data as Record<string, any>).customers) {
                name += ` (${(data as Record<string, any>).customers.company_name})`;
            }
            return name;
        },
        expand: true,
        search(input) {
            const value = `%${input}%`;
            return [{ type: 'AND', field: 'title', value, operator: 'like' }];
        },
        related: [
            { table: 'job_opening_stages', field: 'job_opening_id' },
            {
                table: 'job_opening_role',
                field: 'id',
                parent_field: 'roles',
                clickable: true,
                preload: true,
            },
        ],
    } as DefinitionEntry<JobOpening>,
    job_opening_stages: {
        list: {
            fields: ['id', 'name', 'kind', 'order', 'description', 'job_opening_id'],
            important: ['id', 'name'],
        },
        search(input, options = {}) {
            const value = `%${input}%`;
            const filters: Filter[] = [{ type: 'AND', field: 'name', value, operator: 'like' }];

            if (options.job_opening_id) {
                filters.push({
                    type: 'AND',
                    operator: 'equals',
                    field: 'job_opening_id',
                    value: options.job_opening_id,
                });
            }

            return filters;
        },
        preload(opts = {}) {
            if (opts.job_opening_id) {
                return [
                    {
                        type: 'AND' as const,
                        operator: 'equals',
                        field: 'job_opening_id',
                        value: opts.job_opening_id,
                    },
                ];
            }
            return [];
        },
        display: (data) => data.name,
    } as DefinitionEntry<JobOpeningStage>,
    candidates: {
        disableCreate: true,
        disableDelete: true,
        disableEdit: true,
        list: {
            fields: ['id', 'email', 'user_id'],
            important: ['id', 'email'],
        },
        edit: {
            hide: [
                'ts_interview',
                'application_data',
                'linkedin_resume',
                'linkedin_resume_updated_at',
                'email_normalized',
                'status_history',
            ],
            delete: [
                'ts_interview',
                'application_data',
                'linkedin_resume',
                'linkedin_resume_updated_at',
            ],
            required: ['user_id', 'email'],
            required_marketing: [],
        },
        related: [
            {
                table: 'tags',
                field: 'id',
                parent_field: 'skills',
                clickable: true,
                options: { category: 'skill' },
            },
        ],
        display: (row) => {
            let name = `${row.email}`;
            if ((row as Record<string, any>).users) {
                name = (row as Record<string, any>).users.first_name;
                if ((row as Record<string, any>).users.last_name) {
                    name += ` ${(row as Record<string, any>).users.last_name}`;
                }
                name += ` (${(row as Record<string, any>).users.email})`;
            }

            return name;
        },
        customSearch: async (input, opts = {}) => {
            const candidates = await searchCandidates(input, (opts.roles || []) as string[]);
            const rows = await getUsers(candidates.map((c) => c.user_id as number));

            const users: Record<string, Row> = {};
            for (const u of rows) {
                users[u.id!] = u;
            }

            return candidates.map((e) => {
                e.users = users[String(e.user_id)];
                return e;
            });
        },
    } as DefinitionEntry<Candidate>,
    applications: {
        list: {
            fields: [
                'id',
                'job_opening_id',
                'candidate_id',
                'stage_id',
                'stage_kind',
                'last_stage_change_at',
                'disqualification_reason',
            ],
            important: ['id'],
        },
        expand: true,
        display: (row) => {
            let result = `Application ${row.id}`;
            if ((row as Record<string, any>).candidates) {
                result = (row as Record<string, any>).candidates.email;
            }

            if ((row as Record<string, any>).job_openings) {
                result += `at ${(row as Record<string, any>).job_openings.title}`;
            }

            return result;
        },
    } as DefinitionEntry<Application>,
    contracts: {
        list: {
            fields: ['id', 'user_id', 'customer_id', 'relation', 'status', 'salary'],
            important: ['id', 'user_id'],
            view: {
                termination_reason: (r) => getTerminationReason(r as string),
            },
        },
        related: [
            { table: 'equipment', field: 'contract_id', clickable: true },
            { table: 'raises', field: 'contract_id' },
            { table: 'agreements', field: 'contract_id' },
        ],
        edit: {
            description: { 'ui:widget': 'textarea' },
            hide: ['checkins', 'termination_details', 'management_fee'],
        },
        expand: true,
        disableDelete: true,
        disableEdit: true,
        disableCreate: true,
        display: (row) => {
            let name = `Contract ${row.id}`;
            const r = row as Record<string, any>;
            if (r.users) {
                name = r.users.first_name;
                if (r.users.last_name) {
                    name += ` ${r.users.last_name}`;
                }

                if (r.customers) {
                    return `${name} at ${r.customers.company_name} (${row.status})`;
                }
            }

            name += ` (${row.status})`;

            return name;
        },
        customSearch: async (input) => {
            const rows: Row[] = await search('users', input);
            const users: Record<string, Row> = {};
            for (const row of rows) {
                users[String(row.id)] = row;
            }

            const contracts = await searchTable<Row>({
                table: 'contracts',
                page: 1,
                filters: [
                    { type: 'AND', field: 'user_id', operator: 'any', value: Object.keys(users) },
                ],
            }).then((data) => data.rows || []);

            const customers = (
                await searchTable<Row>({
                    table: 'customers',
                    page: 1,
                    perPage: contracts.length,
                    filters: [
                        {
                            type: 'AND',
                            field: 'id',
                            operator: 'any',
                            value: contracts.map((e) => e.customer_id),
                        },
                    ],
                }).then((data) => data.rows || [])
            ).reduce<Record<string, Row>>((acc, row) => {
                acc[String(row.id)] = row;
                return acc;
            }, {});

            return contracts.map((e) => {
                e.users = users[String(e.user_id)];
                e.customers = customers[String(e.customer_id)];
                return e;
            });
        },
        beforeUpdate(data) {
            if (data.termination_details) {
                delete data['termination_details'];
            }
        },
    } as DefinitionEntry<Contract>,
    expenses: {
        list: {
            fields: [
                'id',
                'title',
                'status',
                'kind',
                'total',
                'contract_id',
                'customer_id',
                'expensed_at',
            ],
            important: ['id', 'title'],
        },
        edit: {
            description: { 'ui:widget': 'textarea' },
        },
    } as DefinitionEntry<Expense>,
    pto_requests: {
        disableCreate: true,
        disableEdit: true,
        disableDelete: true,
        list: {
            fields: ['id', 'contract_id', 'status', 'days', 'start', 'end', 'comments'],
            important: ['id', 'contract_id'],
        },
        edit: {
            comments: { 'ui:widget': 'textarea' },
        },
    } as DefinitionEntry<PTORequest>,
    bank_accounts: {
        list: {
            fields: [
                'id',
                'user_id',
                'routing_number',
                'account_number',
                'iban',
                'swift',
                'cbu',
                'tax_id',
            ],
            important: ['id', 'user_id'],
        },
        disableEdit: true,
        disableCreate: true,
        disableDelete: true,
        disableExport: true,
        details: {
            extend: async (data, schema) => {
                if (data.user_id) {
                    const result = await searchTable<Row>({
                        table: 'users',
                        page: 1,
                        filters: [
                            {
                                type: 'AND',
                                field: 'id',
                                operator: 'equals',
                                value: data.user_id,
                            },
                        ],
                    });
                    const user = (result.rows || []).find((v) => v);
                    (data as Record<string, any>).telephone = user?.phone?.[0];
                    schema.properties.telephone = { type: ['string'], extended: true };
                }
            },
        },
    } as DefinitionEntry<BankAccount>,
    customer_invoices: {
        list: {
            fields: [
                'id',
                'created_at',
                'status',
                'customer_id',
                'month',
                'year',
                'number',
                'amount',
            ],
            important: ['id'],
        },
        disableCreate: true,
        disableEdit: true,
        related: [
            {
                table: 'invoice_lines',
                field: 'invoice_id',
                filters: [
                    { field: 'type', value: 'customer_invoices', operator: 'equals', type: 'AND' },
                ],
            },
        ],
    } as DefinitionEntry<CustomerInvoice>,
    invoice_lines: {
        disableCreate: true,
        disableEdit: true,
        disableDelete: true,
        list: {
            fields: ['id', 'kind', 'concept', 'rate', 'quantity', 'document_id'],
            important: ['concept'],
        },
        edit: {
            hide: ['source_entities'],
        },
        beforeUpdate(data) {
            if (!data.source_entities) {
                data.source_entities = {};
            }
        },
    } as DefinitionEntry<InvoiceLine>,
    users: {
        protected: true,
        list: {
            fields: ['id', 'email', 'first_name', 'last_name', 'roles'],
            important: ['id', 'email'],
            picture: 'picture',
        },
        edit: {
            hide: ['calendar_availability', 'email_normalized', 'personal_info', 'country'],
            picture: { 'ui:widget': 'picture' },
        },
        related: [
            { table: 'contracts', field: 'user_id' },
            { table: 'bank_accounts', field: 'user_id' },
            { table: 'candidates', field: 'user_id' },
        ],
        display: (row) => {
            const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
            return name ? `${name} (${row.email})` : `${row.email}`;
        },
        customSearch: async (input, opts = {}) => {
            const roles = opts.role ? [opts.role] : [];
            return searchUsers(
                input,
                roles as string[],
                opts.customer_id as number,
                Boolean(opts.company_owners),
            );
        },
        preload(opts = {}) {
            if (opts.roles) {
                return [
                    {
                        field: 'roles',
                        operator: 'contains',
                        value: opts.roles,
                        type: 'AND' as const,
                    },
                ];
            }
            return [];
        },
    } as DefinitionEntry<User>,
    pto_policies: {
        disableCreate: true,
        disableEdit: true,
        disableDelete: true,
        list: {
            fields: ['id', 'name', 'kind', 'customer_id', 'number', 'default', 'country'],
            important: ['id', 'name'],
        },
    } as DefinitionEntry<PTOPolicy>,
    startup_applications: {
        list: {
            fields: [
                'id',
                'domain',
                'company_name',
                'token',
                'status',
                'applied_at',
                'crunchbase_id',
            ],
            important: ['id', 'token', 'company_name', 'domain'],
        },
    } as DefinitionEntry<StartupApplication>,
    job_opening_role: {
        list: {
            fields: [
                'id',
                'name',
                'kind',
                'display_onboard_customer',
                'display_onboard_applicant',
                'can_automatch',
                'can_schedule_meeting',
                'skills',
            ],
            important: ['id', 'name'],
        },
        display: (row) => row.name,
        search(input) {
            const value = `%${input}%`;
            return [{ type: 'OR', field: 'name', value, operator: 'like' }];
        },
        related: [
            {
                table: 'tags',
                field: 'id',
                parent_field: 'skills',
                clickable: true,
                options: { category: 'skill' },
            },
        ],
    } as DefinitionEntry<JobOpeningRole>,
    bonuses: {
        disableCreate: true,
        disableEdit: true,
        disableDelete: true,
        list: {
            fields: [
                'id',
                'contract_id',
                'amount',
                'contractor_accounting_status',
                'customer_accounting_status',
                'effective_at',
            ],
        },
        important: ['amount', 'id'],
    } as DefinitionEntry<Bonus>,
    email_templates: {
        list: {
            fields: ['id', 'name', 'subject', 'updated_at'],
        },
        important: ['id', 'name'],
        edit: {
            content: { 'ui:widget': 'text_editor', 'ui:height': 30 },
        },
    } as DefinitionEntry<EmailTemplate>,
    webhooks: {
        important: ['name', 'url'],
    } as DefinitionEntry<Webhook>,
    payments: {
        list: {
            fields: [
                'id',
                'status',
                'contractor_invoice_id',
                'user_id',
                'currency',
                'amount',
                'payment_id',
            ],
            important: ['id'],
        },
        related: [
            { table: 'bank_accounts', field: 'id', parent_field: 'bank_account_id' },
            { table: 'contractor_invoices', field: 'id', parent_field: 'contractor_invoice_id' },
            { table: 'users', field: 'id', parent_field: 'user_id' },
        ],
        disableEdit: true,
        disableCreate: true,
        disableDelete: true,
    } as DefinitionEntry<Payment>,
    recordings: {
        details: {
            // NOTE(toqueteos): this is only temporary, transcripts will be moved out of db
            extend: (data) => {
                const metadata = data.metadata as unknown as Record<string, any>;
                if (metadata?.transcript?.data?.results) {
                    delete metadata.transcript.data.results;
                }
                if (metadata?.transcript?.data?.speakerRanges) {
                    delete metadata.transcript.data.speakerRanges;
                }
            },
        },
        list: {
            fields: [
                'id',
                'source',
                'kind',
                'name',
                'candidate_id',
                'customer_id',
                'interviewer',
                'participant_emails',
                'permalink',
            ],
            important: ['name', 'candidate_id'],
            hide: ['metadata'],
            view: {
                permalink: (r) => getRecordingUrl(r as string),
            },
        },
        edit: {
            hide: ['metadata'],
            delete: ['metadata'],
        },
        related: [
            { table: 'candidates', field: 'id', parent_field: 'candidate_id', clickable: true },
            { table: 'customers', field: 'id', parent_field: 'customer_id', clickable: true },
            { table: 'users', field: 'id', parent_field: 'interviewer_id', clickable: true },
        ],
    } as DefinitionEntry<Recording>,
};

for (const table of Object.keys(definitions)) {
    const entry = definitions[table];
    entry.edit ??= {};
    entry.edit.hide ??= [];
    entry.edit.hide.push('id', 'created_at', 'updated_at', 'deleted_at');
}

const defaultSearch = (input: string): Filter[] => [
    { type: 'AND', operator: 'equals', value: input, field: 'id' },
];

export function getDefinitions<T>(table: string, schema: any): DefinitionEntry<T> {
    const defs = definitions[table] || {};
    return {
        ...defs,
        list: {
            fields: (defs.list?.fields ||
                Object.keys((schema?.properties || {}) as Record<string, unknown>)) as (keyof T)[],
            important: (defs.list?.important || ['id']) as (keyof T)[],
            picture: defs.list?.picture,
            view: defs.list?.view || {},
        },
        protected: defs.protected,
        search: defs.search || defaultSearch,
        display: defs.display || ((row: any) => row.id),
    };
}

export function beforeUpdate(table: string, data: Row): Row {
    const fn = definitions[table]?.beforeUpdate;
    if (fn) {
        fn(data);
    }

    return data;
}

export function extend(table: string, data: Row, schema: any): void {
    const fn = definitions[table]?.details?.extend;
    if (fn) {
        void fn(data, schema);
    }
}
